import CodeBlock from '../CodeBlock';

const EXIT_CODES: Array<[string, string, string]> = [
  ['0', 'ok', 'Success'],
  ['1', 'usage', 'Bad CLI args, unreadable/invalid plan file, or anything rejected before a network call'],
  ['2', 'auth', 'No credential resolved locally, or the backend rejected the one sent'],
  ['3', 'not_found', 'No resource at that id (includes an unknown agent-guide --workflow name)'],
  ['4', 'validation', 'Backend rejected the body — every failure at once in error.detail.problems'],
  ['5', 'insufficient_balance', 'Not enough prepaid balance to cover the launch'],
  ['6', 'remote', 'Any other backend/API error, including a failed/cancelled sweep and feeds run (501)'],
  ['7', 'timeout', 'sweeps launch --wait polled past --timeout without reaching a terminal status'],
];

const COMMANDS: Array<[string, string]> = [
  ['propalgos keys create --name N --scope S…', 'Mint a scoped API key (JWT auth). The secret is shown once.'],
  ['propalgos keys list / revoke <id>', 'List or revoke keys (JWT auth).'],
  ['propalgos keys use <secret>', 'Store a key in the OS keyring. Local only — no network call.'],
  ['propalgos sweeps estimate --plan p.json', 'Size and price a plan offline. No credential needed.'],
  ['propalgos sweeps launch --plan p.json --wait', 'Launch a sweep, optionally polling to a terminal status.'],
  ['propalgos sweeps get / list / cancel', 'Inspect and cancel sweeps.'],
  ['propalgos feeds create --strategy … --execution-url …', 'Create a strategy feed; returns key_id, never the secret.'],
  ['propalgos feeds get / list / disable', 'Inspect feeds; disable is the kill switch.'],
  ['propalgos feeds emissions / emission', 'Read the audit trail; emission does the sha256 provenance check.'],
  ['propalgos feeds run <feed_id>', 'Server-side cycle run — answers 501 until the jobs plan lands.'],
  ['propalgos schema', 'Machine-readable catalog of every command, generated live from the CLI.'],
  ['propalgos agent-guide [--workflow N] [--list]', 'The bundled agent prompt pack and its workflows.'],
];

export default function DocsCLIModule() {
  return (
    <section data-docs-section="cli" className="text-left mb-16">
      <h2 className="text-2xl font-bold text-white mb-2 group">
        <a href="#cli" className="hover:text-sky-400 transition-colors">CLI</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h2>
      <p className="text-gray-400 mb-6">
        Command-line interface available as both <code className="text-gray-300">propalgos</code> and{' '}
        <code className="text-gray-300">pa</code> (alias). Installed automatically with the SDK.
        Local commands (<code className="text-gray-300">version</code>,{' '}
        <code className="text-gray-300">doctor</code>, <code className="text-gray-300">env</code>,{' '}
        <code className="text-gray-300">sweeps estimate</code>) need no credential; everything
        else talks to the platform API.
      </p>

      {/* Output contract */}
      <div id="cli-contract" data-docs-section="cli-contract" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">Output contract</h3>
        <p className="text-gray-400 text-sm mb-3">
          <code className="text-sky-400">--json</code> works in any position — before the
          subcommand, after it, mixed with other flags. Every command then writes exactly one
          JSON document to stdout and nothing else. Anything transient (progress, waiting-for-status)
          goes to stderr, so piping stdout into <code className="text-gray-300">jq</code> is
          always safe. Failures share one shape:
        </p>
        <CodeBlock
          language="json"
          code={`{"error": {"code": "...", "message": "...", "retryable": false,
           "correlation_id": "...", "detail": null}}`}
        />
        <p className="text-gray-400 text-sm">
          <code className="text-gray-300">sweeps launch --wait</code> adds{' '}
          <code className="text-gray-300">error.sweep_id</code> on a remote/timeout failure —
          the job is still real and still billing, so you get its handle back even though the
          launch call didn&apos;t succeed.
        </p>
      </div>

      {/* Exit codes */}
      <div id="cli-exit-codes" data-docs-section="cli-exit-codes" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-3">Exit codes</h3>
        <p className="text-gray-400 text-sm mb-3">
          The same eight, every command, no per-command exceptions.
        </p>
        <div className="overflow-x-auto">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Code</th>
                <th className="py-2 pr-4 text-gray-400 font-medium">Name</th>
                <th className="py-2 text-gray-400 font-medium">Meaning</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {EXIT_CODES.map(([code, name, meaning]) => (
                <tr key={code} className="border-b border-gray-800/50 last:border-0">
                  <td className="py-1.5 pr-4 font-mono text-amber-400">{code}</td>
                  <td className="py-1.5 pr-4 font-mono text-sky-400">{name}</td>
                  <td className="py-1.5 text-gray-400">{meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Auth */}
      <div id="cli-auth" data-docs-section="cli-auth" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">Auth</h3>
        <p className="text-gray-400 text-sm mb-3">
          Two credential shapes. An <strong className="text-gray-300">API key</strong>{' '}
          (<code className="text-gray-300">pa_live_…</code>) covers everything under{' '}
          <code className="text-gray-300">sweeps</code> and{' '}
          <code className="text-gray-300">feeds</code>, resolved in order:{' '}
          <code className="text-gray-300">--api-key</code>,{' '}
          <code className="text-gray-300">PROPALGOS_API_KEY</code>, OS keyring. A{' '}
          <strong className="text-gray-300">JWT</strong> (
          <code className="text-gray-300">PROPALGOS_TOKEN</code> or{' '}
          <code className="text-gray-300">--token</code>) is required for{' '}
          <code className="text-gray-300">keys create/list/revoke</code> — an API key can never
          mint or revoke another key, enforced server-side.
        </p>
        <CodeBlock
          language="bash"
          code={`$ export PROPALGOS_TOKEN=<your session jwt>
$ propalgos keys create --name agent-worker \\
    --scope sweeps:read --scope sweeps:write \\
    --scope feeds:read --scope feeds:write --json

$ propalgos keys use pa_live_REPLACEWITHTHESECRETSHOWNABOVE
# or: export PROPALGOS_API_KEY=pa_live_…
# point elsewhere with PROPALGOS_API_URL`}
        />
      </div>

      {/* Command map */}
      <div id="cli-commands" data-docs-section="cli-commands" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-3">Commands</h3>
        <div className="overflow-x-auto">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Command</th>
                <th className="py-2 text-gray-400 font-medium">Purpose</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {COMMANDS.map(([cmd, desc]) => (
                <tr key={cmd} className="border-b border-gray-800/50 last:border-0">
                  <td className="py-1.5 pr-4 font-mono text-sky-400 whitespace-nowrap">{cmd}</td>
                  <td className="py-1.5 text-gray-400">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-gray-400 text-sm mt-3">
          <code className="text-gray-300">propalgos schema --json</code> is generated live from
          the running CLI, so it cannot drift. If any doc disagrees with it, the schema is right.
        </p>
      </div>

      {/* Local commands */}
      <div className="space-y-6">
        <div>
          <h3 className="text-base font-semibold text-white mb-2">propalgos version</h3>
          <p className="text-gray-400 text-sm mb-2">Print the installed SDK version.</p>
          <CodeBlock
            code={`$ propalgos version
propalgos 0.4.0`}
            language="bash"
          />
        </div>

        <div>
          <h3 className="text-base font-semibold text-white mb-2">propalgos doctor</h3>
          <p className="text-gray-400 text-sm mb-2">
            Verify SDK dependencies and installation health. Checks core packages
            (numpy, pandas, httpx, yfinance) and optional ones (plotly, keyring).
          </p>
          <CodeBlock
            code={`$ propalgos doctor
propalgos 0.4.0

Core:
  ✓ numpy 2.2.3
  ✓ pandas 2.2.3
  ✓ httpx 0.28.1
  ✓ yfinance 0.2.51

Optional:
  ✓ plotly 6.0.1
  ○ keyring — pip install 'propalgos[keyring]'`}
            language="bash"
          />
        </div>

        <div>
          <h3 className="text-base font-semibold text-white mb-2">propalgos env</h3>
          <p className="text-gray-400 text-sm mb-2">
            Print runtime environment summary — GPU hardware, ML frameworks,
            cloud provider, and instance metadata.
          </p>
          <CodeBlock
            code={`$ propalgos env

PropAlgos Environment
────────────────────────────────────────
  Runtime:      PropAlgos Cloud (AWS, us-east-1, g5.xlarge)
  Python:       3.12.4
  GPU:          NVIDIA A10G (24 GB) — CUDA 12.8, driver 570.86
  Frameworks:   PyTorch 2.5.1 (GPU ✓)`}
            language="bash"
          />
        </div>
      </div>
    </section>
  );
}
